import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import conversationService from '../../services/conversationService';
import type {
  ConversationLossReasonFilter,
  ConversationStats,
  DeclineReason,
} from '../../services/conversationService';
import {
  buildConversationLeadStatusFunnelData,
  ConversationLeadStatus,
  getConversationLeadStatusLabel,
  getConversationLeadStatusScope,
} from './conversationLeadStatus';

interface ConversationFunnelCardProps {
  selectedStatus?: ConversationLeadStatus;
  selectedLossReason?: ConversationLossReasonFilter;
  selectedDeclineReason?: DeclineReason;
  onStatusSelect: (status?: ConversationLeadStatus) => void;
  onLossReasonSelect: (lossReason?: ConversationLossReasonFilter) => void;
  onDeclineReasonSelect: (declineReason?: DeclineReason) => void;
  refreshTrigger?: number;
}

const ConversationFunnelCard: React.FC<ConversationFunnelCardProps> = ({
  selectedStatus,
  selectedLossReason,
  selectedDeclineReason,
  onStatusSelect,
  onLossReasonSelect,
  onDeclineReasonSelect,
  refreshTrigger,
}) => {
  const [stats, setStats] = useState<ConversationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const nextStats = await conversationService.getConversationStats();
      setStats(nextStats);
    } catch (err) {
      console.error('Error loading conversation funnel stats:', err);
      setError('No fue posible cargar el embudo de conversaciones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshTrigger]);

  const funnelBands = useMemo(
    () =>
      buildConversationLeadStatusFunnelData(
        stats?.lead_status_distribution,
        stats?.loss_reason_distribution,
        stats?.decline_reason_distribution
      ),
    [stats]
  );
  const selectedStatuses = useMemo(
    () => (selectedStatus ? getConversationLeadStatusScope(selectedStatus) : []),
    [selectedStatus]
  );
  const hasSelectedScope = selectedStatuses.length > 0;
  const minBandWidthPercent = 60;
  const maxBandWidthPercent = 100;
  const widthStep =
    funnelBands.mainBands.length > 1
      ? (maxBandWidthPercent - minBandWidthPercent) / (funnelBands.mainBands.length - 1)
      : 0;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Embudo de Conversaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Conteo acumulado del recorrido comercial actual
          </Typography>
        </Box>

        {(selectedStatus || selectedLossReason || selectedDeclineReason) && (
          <Button
            size="small"
            onClick={() => {
              if (selectedLossReason) {
                onLossReasonSelect(undefined);
              } else if (selectedDeclineReason) {
                onDeclineReasonSelect(undefined);
              } else {
                onStatusSelect(undefined);
              }
            }}
          >
            Limpiar
          </Button>
        )}
      </Stack>

      {loading && !stats ? (
        <Box minHeight={260} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={loadStats}>
              Reintentar
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr minmax(0, 430px) 1fr' },
            alignItems: 'center',
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ width: '100%', maxWidth: 430, justifySelf: 'center' }}>
            <Stack spacing={1.25} alignItems="center">
              {funnelBands.mainBands.map((band, index) => {
                const isSelectedBand = selectedStatus === band.value;
                const isIncludedInScope = selectedStatuses.includes(band.value);
                const widthPercent = maxBandWidthPercent - widthStep * index;
                const filteredStatusesLabel = getConversationLeadStatusScope(band.value)
                  .map((status) => getConversationLeadStatusLabel(status))
                  .join(', ');

                return (
                  <Tooltip
                    key={band.value}
                    arrow
                    placement="left"
                    title={
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {band.label}
                        </Typography>
                        <Typography variant="body2">{band.description}</Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Acumulado: {band.cumulativeCount} | Exacto: {band.exactCount}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Filtra: {filteredStatusesLabel}
                        </Typography>
                      </Box>
                    }
                  >
                    <ButtonBase
                      onClick={() => onStatusSelect(isSelectedBand ? undefined : band.value)}
                      aria-label={`Filtrar por ${band.label}`}
                      aria-pressed={isIncludedInScope}
                      sx={{
                        width: `${widthPercent}%`,
                        maxWidth: '100%',
                        borderRadius: 2.5,
                        px: 2,
                        py: 1.5,
                        justifyContent: 'center',
                        clipPath: 'polygon(6% 0, 94% 0, 100% 100%, 0 100%)',
                        border: '1px solid',
                        borderWidth: isSelectedBand ? 2 : 1,
                        borderColor: isIncludedInScope
                          ? band.color
                          : alpha(band.color, hasSelectedScope ? 0.16 : 0.25),
                        backgroundColor: isSelectedBand
                          ? alpha(band.color, 0.24)
                          : isIncludedInScope
                            ? alpha(band.color, 0.17)
                            : alpha(band.color, hasSelectedScope ? 0.05 : 0.09),
                        boxShadow: isSelectedBand
                          ? `0 10px 24px ${alpha(band.color, 0.18)}`
                          : isIncludedInScope
                            ? `0 6px 18px ${alpha(band.color, 0.12)}`
                            : 'none',
                        opacity: hasSelectedScope && !isIncludedInScope ? 0.72 : 1,
                        transition:
                          'transform 140ms ease, background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          backgroundColor: alpha(
                            band.color,
                            isIncludedInScope ? 0.22 : hasSelectedScope ? 0.08 : 0.16
                          ),
                        },
                      }}
                    >
                      <Stack alignItems="center" spacing={0.25} sx={{ width: '100%', textAlign: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>
                          {band.label}
                        </Typography>
                        <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                          {band.cumulativeCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Exacto: {band.exactCount}
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ justifySelf: { xs: 'stretch', md: 'start' }, width: { xs: '100%', md: 260 } }}>
            <Tooltip
              arrow
              placement="top"
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {funnelBands.sideBand.label}
                  </Typography>
                  <Typography variant="body2">{funnelBands.sideBand.description}</Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Exacto: {funnelBands.sideBand.exactCount}
                  </Typography>
                  {funnelBands.lostReasonBands.length > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                      {funnelBands.lostReasonBands.map((band) => (
                        <Typography key={band.value} variant="caption" display="block">
                          {band.label}: {band.count}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              }
            >
              <ButtonBase
                onClick={() =>
                  onStatusSelect(selectedStatus === funnelBands.sideBand.value ? undefined : funnelBands.sideBand.value)
                }
                aria-label={`Filtrar por ${funnelBands.sideBand.label}`}
                aria-pressed={selectedStatuses.includes(funnelBands.sideBand.value)}
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  px: 2,
                  py: 1.75,
                  border: '1px dashed',
                  borderColor:
                    selectedStatuses.includes(funnelBands.sideBand.value)
                      ? funnelBands.sideBand.color
                      : alpha(funnelBands.sideBand.color, 0.45),
                  backgroundColor:
                    selectedStatuses.includes(funnelBands.sideBand.value)
                      ? alpha(funnelBands.sideBand.color, 0.16)
                      : alpha(funnelBands.sideBand.color, 0.08),
                  transition: 'transform 140ms ease, background-color 140ms ease, border-color 140ms ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    backgroundColor: alpha(funnelBands.sideBand.color, 0.14),
                  },
                }}
              >
                <Stack alignItems="center" spacing={0.25}>
                  <Typography variant="overline" color="text.secondary" lineHeight={1.2}>
                    Fuera del embudo
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {funnelBands.sideBand.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                    {funnelBands.sideBand.exactCount}
                  </Typography>
                </Stack>
              </ButtonBase>
            </Tooltip>

            {funnelBands.lostReasonBands.length > 0 && (
              <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                {funnelBands.lostReasonBands.map((band) => {
                  const isSelectedReason = selectedLossReason === band.value;

                  return (
                    <ButtonBase
                      key={band.value}
                      onClick={() =>
                        onLossReasonSelect(isSelectedReason ? undefined : band.value)
                      }
                      aria-label={`Filtrar por ${band.label}`}
                      aria-pressed={isSelectedReason}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.5,
                        border: '1px solid',
                        borderColor: isSelectedReason ? band.color : 'transparent',
                        backgroundColor: isSelectedReason ? alpha(band.color, 0.12) : 'transparent',
                        transition: 'background-color 140ms ease, border-color 140ms ease',
                        '&:hover': {
                          backgroundColor: alpha(band.color, isSelectedReason ? 0.16 : 0.08),
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: band.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" noWrap title={band.description}>
                            {band.label}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={700}>
                          {band.count}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          mt: 0.35,
                          height: 4,
                          borderRadius: 999,
                          backgroundColor: alpha(band.color, 0.16),
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${
                              band.count > 0 && funnelBands.sideBand.exactCount > 0
                                ? Math.max(4, (band.count / funnelBands.sideBand.exactCount) * 100)
                                : 0
                            }%`,
                            backgroundColor: band.color,
                          }}
                        />
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Stack>
            )}

            {funnelBands.declineReasonBands.length > 0 && (
              <Stack spacing={0.35} sx={{ mt: 1.25, maxHeight: 190, overflowY: 'auto', pr: 0.25 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Causa de no venta
                </Typography>
                {funnelBands.declineReasonBands.map((reason) => {
                  const isSelectedReason = selectedDeclineReason === reason.label;

                  return (
                    <ButtonBase
                      key={reason.label}
                      onClick={() =>
                        onDeclineReasonSelect(isSelectedReason ? undefined : reason.label)
                      }
                      aria-label={`Filtrar por causa ${reason.label}`}
                      aria-pressed={isSelectedReason}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.5,
                        border: '1px solid',
                        borderColor: isSelectedReason ? funnelBands.sideBand.color : 'transparent',
                        backgroundColor: isSelectedReason
                          ? alpha(funnelBands.sideBand.color, 0.12)
                          : 'transparent',
                        transition: 'background-color 140ms ease, border-color 140ms ease',
                        '&:hover': {
                          backgroundColor: alpha(funnelBands.sideBand.color, isSelectedReason ? 0.16 : 0.08),
                        },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography variant="caption" color="text.secondary" title={reason.label} noWrap>
                          {reason.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                          {reason.count}
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        El embudo muestra conteos acumulados. Perdido solo suma en Nuevo. Al hacer clic en una
        banda principal, la tabla se filtra por ese estado y los posteriores.
      </Typography>
    </Box>
  );
};

export default ConversationFunnelCard;
